/**
 * Tax planning as a service: the tiers a firm prices it at, the figures that suggest a client
 * would benefit, and when each accepted strategy has to be done by.
 */
import { z } from "zod";

export const PLANNING_TIERS = ["individual", "business_owner", "multi_entity"] as const;
export type PlanningTierKey = (typeof PLANNING_TIERS)[number];

export const PLANNING_TIER_LABELS: Record<PlanningTierKey, { label: string; description: string }> = {
  individual: { label: "Individual", description: "Wages, investments and retirement accounts" },
  business_owner: { label: "Business owner", description: "One business (Schedule C, S corporation or partnership) with the owner's return" },
  multi_entity: { label: "Multiple entities", description: "Several businesses, rentals or trusts planned together" },
};

const fee = z.number().int().min(0).max(99_999_999).nullable();

export const planningSettingsSchema = z
  .object({
    // The fee for each tier; null until the firm sets its pricing.
    tiers: z.object({ individual: z.object({ feeCents: fee }).strict(), business_owner: z.object({ feeCents: fee }).strict(), multi_entity: z.object({ feeCents: fee }).strict() }).strict(),
    // Clients with an active CFO and advisory plan get a plan each year at no extra fee.
    includedWithCfo: z.boolean(),
    // When a client is suggested for planning.
    thresholds: z
      .object({
        businessProfitCents: z.number().int().min(0).max(99_999_999_999),
        balanceDueCents: z.number().int().min(0).max(99_999_999_999),
        incomeGrowthPct: z.number().min(0).max(1000),
      })
      .strict(),
  })
  .strict();
export type PlanningSettings = z.infer<typeof planningSettingsSchema>;

export const DEFAULT_PLANNING_SETTINGS: PlanningSettings = {
  tiers: { individual: { feeCents: null }, business_owner: { feeCents: null }, multi_entity: { feeCents: null } },
  includedWithCfo: true,
  thresholds: { businessProfitCents: 6_000_000, balanceDueCents: 300_000, incomeGrowthPct: 25 },
};

/** The firm's planning settings, with the defaults for anything not set. */
export function planningSettings(raw: unknown): PlanningSettings {
  const parsed = planningSettingsSchema.safeParse(raw);
  return parsed.success ? parsed.data : DEFAULT_PLANNING_SETTINGS;
}

export type PlanningReason = "balance_due" | "business_profit" | "income_growth" | "return_accepted" | "cfo_included";
export const PLANNING_REASON_LABELS: Record<PlanningReason, string> = {
  balance_due: "Owed tax with the last return",
  business_profit: "Business profit high enough to consider an S corporation",
  income_growth: "Revenue growing quickly",
  return_accepted: "Return just accepted; no plan for this year",
  cfo_included: "CFO plan includes tax planning",
};

/**
 * Who files the business return a strategy depends on: an S corporation (1120-S) or partnership
 * (1065), or the owner's own return (a sole proprietor's Schedule C, and every individual client).
 */
export type PlanningEntity = "individual" | "s_corp" | "partnership";

/** What a strategy's date and wording depend on: the business's return and the state (MD, VA, DC...). */
export type StrategyContext = { entity?: PlanningEntity; state?: string | null };

/** The business's return due date for a calendar year, before any extension, as "MM-DD". */
const returnDue = (entity: PlanningEntity) => (entity === "individual" ? "04-15" : "03-15");

/**
 * When a strategy in an accepted plan has to be done, for the plan's first year (a calendar tax
 * year). The dates are the unextended deadlines; a date on a weekend or holiday moves to the next
 * business day, which only makes these a day or two early.
 *
 * - S corporation election (Form 2553): within 2 months and 15 days of the start of the tax year,
 *   March 15 for an existing calendar-year business (the task text gives the general rule).
 * - SEP and solo 401(k) employer contributions: the business's return due date, including
 *   extensions. March 15 of the next year for an S corporation or partnership, April 15 for a
 *   sole proprietor; the task notes the later date if the return is extended.
 * - HSA: April 15 of the next year (the individual return's due date, without extensions).
 * - Pass-through entity tax: December 31, because the business deducts the tax in the year it
 *   pays it (IRS Notice 2020-75); the election itself has state-specific rules (see the task).
 * - Everything else is a year-end move.
 */
export function strategyDueOn(key: string, year: number, ctx: StrategyContext = {}): string {
  if (key === "s_corp_election") return `${year}-03-15`;
  if (key === "retirement_plan_self_employed") return `${year + 1}-${returnDue(ctx.entity ?? "individual")}`;
  if (key === "hsa") return `${year + 1}-04-15`;
  return `${year}-12-31`;
}

const ENTITY_RETURN: Record<PlanningEntity, string> = {
  individual: "the owner's return (April 15 for a calendar year)",
  s_corp: "the S corporation's return (March 15 for a calendar year)",
  partnership: "the partnership's return (March 15 for a calendar year)",
};

/**
 * The firm's tasks for a strategy, worded for the client's business and state. Only rules
 * confirmed from official sources are stated; anything else says what to confirm.
 */
export function strategyFirmTasks(key: string, ctx: StrategyContext = {}): string[] {
  const entity = ctx.entity ?? "individual";
  const state = (ctx.state ?? "").toUpperCase();
  switch (key) {
    case "s_corp_election":
      return [
        "File Form 2553 within 2 months and 15 days of the start of the tax year (March 15 for a calendar-year business); a late election needs relief under Rev. Proc. 2013-30",
        "Set up payroll for the owner's salary",
      ];
    case "retirement_plan_self_employed":
      return [`Help the client open and fund the retirement plan by the due date of ${ENTITY_RETURN[entity]}, or by the extended due date if the return is extended`];
    case "ptet":
      if (state === "MD")
        return [
          "Make the Maryland pass-through entity tax election (made with the first Form 510/511D, 510/511E or 511 for the year; it can't be changed later) and pay the tax by December 31 to deduct it this year",
        ];
      if (state === "VA")
        return [
          "Make the Virginia pass-through entity tax election (by an estimated or extension payment, or Form 502PTET by the extended due date) and pay the tax by December 31 to deduct it this year",
        ];
      return ["Make the pass-through entity tax payments by December 31 to deduct them this year; confirm the state's election deadline"];
    default:
      return STRATEGY_FIRM_TASKS[key] ?? [];
  }
}

/** The follow-up a strategy needs from the firm, beyond the client's action item (general wording; see strategyFirmTasks). */
export const STRATEGY_FIRM_TASKS: Record<string, string[]> = {
  s_corp_election: [
    "File Form 2553 within 2 months and 15 days of the start of the tax year (March 15 for a calendar-year business); a late election needs relief under Rev. Proc. 2013-30",
    "Set up payroll for the owner's salary",
  ],
  retirement_plan_self_employed: ["Help the client open and fund the retirement plan by the business's return due date, or by the extended due date if the return is extended"],
  ptet: ["Make the pass-through entity tax payments by December 31 to deduct them this year; confirm the state's election deadline"],
  augusta_rule: ["Document the home rental to the business (dates, rate and minutes)"],
};
